import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import FooterCopyright from '../components/FooterCopyright'
import { contactApi } from '../services/api'

const inputClasses =
  'w-full px-4 py-3 bg-white border border-paper-200 rounded-sm text-ink-900 text-base outline-none box-border placeholder:text-ink-300 focus:border-accent-500 transition-colors duration-150'

const ContactPage: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot — hidden from real users
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'submitting') return

    const trimmedEmail = email.trim()
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.')
      return
    }
    if (!message.trim()) {
      setError('Please enter a message.')
      return
    }

    setError('')
    setStatus('submitting')
    try {
      await contactApi.submit({
        name: name.trim() || undefined,
        email: trimmedEmail,
        message: message.trim(),
        website,
      })
      setStatus('success')
    } catch {
      setError('Something went wrong sending your message. Please try again.')
      setStatus('idle')
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-paper-0">

      {/* Header */}
      <header className="flex h-16 items-center justify-between border-b border-white/10 bg-brand-950 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center">
          <img src="/assets/logo-white.png" alt="Treu" className="h-[26px] w-auto" />
        </Link>
        <Link
          to="/login"
          className="rounded-sm border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
        >
          Sign In
        </Link>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-[560px] flex-grow px-4 pb-20 pt-12">

        {status === 'success' ? (
          <div className="text-center pt-10">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
              <svg className="h-7 w-7 text-accent-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h1 className="mb-3 text-2xl font-bold text-ink-900 md:text-3xl">
              Message sent.
            </h1>
            <p className="mx-auto max-w-[400px] text-[0.9375rem] leading-relaxed text-ink-600">
              Thanks for reaching out. We'll get back to you at {email.trim()}.
            </p>
            <Link
              to="/"
              className="mt-8 inline-block text-sm text-accent-600 underline transition-colors hover:text-accent-500"
            >
              ← Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <p className="font-display mb-4 text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
                Contact
              </p>
              <h1 className="mb-3 text-2xl font-bold text-ink-900 md:text-4xl">
                Get in touch.
              </h1>
              <p className="text-[0.9375rem] leading-relaxed text-ink-600">
                Questions, feedback, or trouble with your account — send us a message and we'll get back to you.
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-md border border-danger-500/25 bg-danger-100 p-4 text-sm text-ink-900">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="contact-name">
                Name <span className="font-normal text-ink-400">(optional)</span>
              </label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                autoComplete="name"
                className={`${inputClasses} mb-5`}
              />

              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="contact-email">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className={`${inputClasses} mb-5`}
              />

              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="contact-message">
                Message
              </label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={5}
                required
                className={`${inputClasses} mb-8 resize-y`}
              />

              {/* Honeypot — hidden from people, filled by bots */}
              <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full cursor-pointer rounded-md bg-accent-500 px-6 py-4 text-sm font-semibold text-white transition-colors duration-150 hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-brand-950 px-4 py-8 text-center">
        <div className="mb-4 flex flex-wrap justify-center gap-8">
          <Link to="/" className="text-sm text-white/50 transition-colors hover:text-white/80">Home</Link>
          <Link to="/terms" className="text-sm text-white/50 transition-colors hover:text-white/80">Terms of Service</Link>
          <Link to="/privacy" className="text-sm text-white/50 transition-colors hover:text-white/80">Privacy Policy</Link>
        </div>
        <FooterCopyright variant="full" className="text-white/50" />
      </footer>

    </div>
  )
}

export default ContactPage
